import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/shared/store/authStore'
import { useEnvStore } from '@/shared/store/envStore'
import { issueAiInitToken } from './api'

// 주소는 로그인 corpCd 로 정해진 환경(AP/DEV)을 따라간다 — envStore 참조.
const aiUrl = () => useEnvStore.getState().aiUrl
const aiOrigin = () => resolveOrigin(aiUrl())
const AI_INIT_TOKEN_REFRESH_WINDOW_MS = 60_000
const MAIL_DRAFT_RETRY_DELAYS_MS = [1200, 2500, 4000, 6500, 9000, 12000, 16000, 20000]
function resolveOrigin(url: string | undefined | null): string {
  if (!url) return ''
  try {
    return new URL(url).origin
  } catch {
    return ''
  }
}

function cachedInitToken(): string | null {
  const { aiInitToken, aiInitTokenExpiresAt } = useAuthStore.getState()
  if (!aiInitToken || !aiInitTokenExpiresAt) return null

  const expiresAtMs =
    aiInitTokenExpiresAt < 1_000_000_000_000 ? aiInitTokenExpiresAt * 1000 : aiInitTokenExpiresAt
  return expiresAtMs - Date.now() > AI_INIT_TOKEN_REFRESH_WINDOW_MS ? aiInitToken : null
}

async function buildAssistantUrl(): Promise<string> {
  const store = useAuthStore.getState()
  const user = store.user
  let initToken = cachedInitToken()

  if (!initToken) {
    const ai = await issueAiInitToken()
    initToken = ai.initToken
    store.setAiInitToken(ai.initToken, ai.expiresAt)
  }

  const url = new URL(aiUrl())
  url.searchParams.set('sourceSystem', 'QSCV')
  url.searchParams.set('corpCd', user?.corpCd ?? '')
  url.searchParams.set('userId', user?.userId ?? '')
  url.searchParams.set('userNm', user?.userNm ?? '')
  url.hash = `initToken=${initToken}`
  return url.toString()
}

/** AI 어시스턴트(외부 앱) iframe. 패널/풀페이지가 공유. onClose 는 iframe 닫기 신호로 호출됨. */
export function AiAssistantFrame({ onClose }: { onClose: () => void }) {
  const [src, setSrc] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const mailComposeWindowRef = useRef<Window | null>(null)
  const mailComposeMailIdRef = useRef<string | null>(null)
  // 열릴 때 토큰 새로 발급(30분 만료 회피) 후 iframe URL 구성
  useEffect(() => {
    let alive = true
    buildAssistantUrl()
      .then((url) => alive && setSrc(url))
      .catch(() => alive && setError(true))
    return () => {
      alive = false
    }
  }, [])

  const postResultToFrame = useCallback((message: Record<string, unknown>) => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    win.postMessage(message, aiOrigin())
  }, [])
  const postErrorToFrame = useCallback(
    (requestId: string | undefined, message: string) => {
      postResultToFrame({
        source: 'VMERP_AI_HOST',
        type: 'VMERP_AI_ERROR',
        requestId: requestId || '',
        message,
      })
    },
    [postResultToFrame],
  )

  // 20260806_한경수_AI 챗 iframe의 토큰 재발급 요청(VMERP_AI_REFRESH_INIT_TOKEN)에 응답하는 핸들러가 없어
  // 매번 30초 브릿지 타임아웃으로 이어지던 문제 수정 - 기존 issueAiInitToken() 재사용해 응답
  const refreshInitToken = useCallback(
    async (requestId: string | undefined) => {
      try {
        const ai = await issueAiInitToken()
        useAuthStore.getState().setAiInitToken(ai.initToken, ai.expiresAt)
        postResultToFrame({
          source: 'VMERP_AI_HOST',
          type: 'VMERP_AI_INIT_TOKEN',
          requestId: requestId || '',
          payload: { initToken: ai.initToken },
        })
      } catch {
        postErrorToFrame(requestId, 'AI 연결 정보를 갱신하지 못했습니다.')
      }
    },
    [postResultToFrame, postErrorToFrame],
  )

  const postMailDraftToCompose = useCallback(
    (popup: Window, draftText: string, targetOrigin: string, requestId: string | undefined) => {
      try {
        popup.postMessage({ type: 'sendData', data: { content: draftText } }, targetOrigin)
        postResultToFrame({
          source: 'VMERP_AI_HOST',
          type: 'VMERP_AI_OPEN_MAIL_COMPOSE_RESULT',
          requestId: requestId || '',
          payload: { ok: true },
        })
      } catch {
        postErrorToFrame(requestId, '답장 초안 반영에 실패했습니다.')
      }
    },
    [postResultToFrame, postErrorToFrame],
  )

  const openMailCompose = useCallback(
    (
      payload: { mailId?: string; userEmail?: string; draftText?: string },
      requestId: string | undefined,
    ) => {
      const groupwareOrigin = resolveOrigin(useEnvStore.getState().groupwareUrl)
      if (!groupwareOrigin) {
        postErrorToFrame(requestId, '그룹웨어 주소가 설정되지 않아 답장 작성창을 열 수 없습니다.')
        return
      }
      const mailId = String(payload.mailId || '')
      const userEmail = String(payload.userEmail || '')
      const draftText = String(payload.draftText || '')
      if (!draftText) {
        postErrorToFrame(requestId, '반영할 답장 초안이 없습니다.')
        return
      }
      const existing = mailComposeWindowRef.current
      if (existing && !existing.closed && mailComposeMailIdRef.current === mailId) {
        existing.focus()
        postMailDraftToCompose(existing, draftText, groupwareOrigin, requestId)
        return
      }

      const popupWidth = 1300
      const popupHeight = 804
      const popupX = Math.ceil((window.screen.width - popupWidth) / 2)
      const popupY = Math.ceil((window.screen.height - popupHeight) / 2)
      const options = `scrollbars=no, resizable=yes, width=${popupWidth}, height=${popupHeight}, top=${popupY}, left=${popupX}`
      const url =
        groupwareOrigin +
        '/mail/new?mode=write&from=re&mailId=' +
        encodeURIComponent(mailId) +
        '&userEmail=' +
        encodeURIComponent(userEmail) +
        '&type=popup&fullHeight=true&_ts=' +
        Date.now()

      const popup = window.open(url, '_blank', options)
      if (!popup) {
        postErrorToFrame(requestId, '답장 작성창을 열지 못했습니다. 팝업 차단을 확인해 주세요.')
        return
      }
      mailComposeWindowRef.current = popup
      mailComposeMailIdRef.current = mailId
      // works 팝업 자체 초기화(관리자설정 조회, 서명/원본인용구 셋팅)가 비동기라 언제 준비되는지
      // 알 방법이 없어(다른 origin), 여러 시점에 나눠 재전송해 살아남을 확률을 높인다.
      MAIL_DRAFT_RETRY_DELAYS_MS.forEach((delay) => {
        window.setTimeout(() => {
          if (!popup.closed) postMailDraftToCompose(popup, draftText, groupwareOrigin, requestId)
        }, delay)
      })
    },
    [postErrorToFrame, postMailDraftToCompose],
  )

  // iframe(AI 어시스턴트) 닫기 버튼 → VMERP_AI_CLOSE postMessage 받아 닫기.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      let raw = ''
      try {
        raw = typeof e.data === 'string' ? e.data : JSON.stringify(e.data ?? '')
      } catch {
        // ponytail: DevTools/HMR 등 순환 참조 메시지는 무시. stringify throw 시 아래가 다 죽던 문제.
        return
      }
      if (raw.includes('VMERP_AI_CLOSE')) {
        onCloseRef.current()
        return
      }
      const message = e.data as {
        source?: string
        type?: string
        requestId?: string
        payload?: unknown
      } | null
      if (!message || message.source !== 'VMERP_AI_IFRAME') return
      if (message.type === 'VMERP_AI_OPEN_MAIL_COMPOSE') {
        openMailCompose(
          (message.payload as { mailId?: string; userEmail?: string; draftText?: string }) || {},
          message.requestId,
        )
      }
      // 20260806_한경수_토큰 재발급 요청 응답 처리 추가
      if (message.type === 'VMERP_AI_REFRESH_INIT_TOKEN') {
        refreshInitToken(message.requestId)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [openMailCompose, refreshInitToken])

  const handshake = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
    // iframe에 host origin을 알려야 닫기(VMERP_AI_CLOSE) postMessage가 돌아옴.
    // ponytail: 리스너 등록 타이밍 경합 회피용 반복 전송. 핸드셰이크 ack 생기면 그걸로 교체.
    iframeRef.current = e.currentTarget
    const win = e.currentTarget.contentWindow
    const target = aiOrigin()
    const msg = { source: 'VMERP_AI_HOST', type: 'VMERP_AI_INIT', payload: {} }
    let n = 0
    const id = setInterval(() => {
      win?.postMessage(msg, target)
      if (++n >= 10) clearInterval(id)
    }, 300)
  }, [])

  if (error)
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-neutral-500">
        AI 어시스턴트를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
      </div>
    )
  if (!src)
    return (
      <div className="flex h-full items-center justify-center text-sm text-neutral-400">
        불러오는 중…
      </div>
    )
  return (
    <iframe
      src={src}
      title="bizbee AI Assistant"
      className="h-full w-full border-0"
      allow="clipboard-write"
      onLoad={handshake}
    />
  )
}
